import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { GoogleSignIn } from '../components/GoogleSignIn';

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim();
const ALLOWED_DOMAIN = (import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN ?? 'vasudevprintpack.com').trim();

export function LoginPage() {
  const { user, login, loginWithGoogle, loading } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login(username, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'login_failed');
    }
  }

  async function onGoogleCredential(credential: string) {
    setError(null);
    try {
      await loginWithGoogle(credential);
      navigate('/dashboard');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'login_failed';
      setError(
        msg === 'domain_not_allowed'
          ? `Only @${ALLOWED_DOMAIN} accounts are allowed.`
          : msg === 'email_not_verified'
          ? 'Your Google account email is not verified.'
          : msg === 'google_oauth_not_configured'
          ? 'Google sign-in is not configured on the server.'
          : msg,
      );
    }
  }

  function friendlyError(raw: string): string {
    return raw === 'invalid_credentials' ? 'Wrong username or password.' : raw;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-50 px-4">
      <div className="card w-full max-w-sm">
        <div className="card-header">VASUDEV PRINT PACK — Sign in</div>
        <div className="card-body space-y-4">
          {GOOGLE_CLIENT_ID ? (
            <>
              <GoogleSignIn
                clientId={GOOGLE_CLIENT_ID}
                onCredential={onGoogleCredential}
                onError={(m) => setError(m)}
              />
              <p className="text-center text-[11px] text-slate-500">
                Restricted to @{ALLOWED_DOMAIN} accounts.
              </p>
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-[11px] uppercase tracking-wider text-slate-400">or</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
            </>
          ) : null}

          <form className="space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="label" htmlFor="u">Username</label>
              <input
                id="u" className="input" autoComplete="username"
                value={username} onChange={(e) => setUsername(e.target.value)} required
              />
            </div>
            <div>
              <label className="label" htmlFor="p">Password</label>
              <input
                id="p" type="password" className="input" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)} required
              />
            </div>
            {error && (
              <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200">
                {friendlyError(error)}
              </div>
            )}
            <button className="btn-primary w-full" disabled={loading} type="submit">
              {loading ? 'Signing in…' : 'Sign in with password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
